import { useEffect, useRef, useState } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import {
  addDays,
  addMonths,
  formatMonthLabel,
  getMonthGrid,
  isSameDay,
  parseISODate,
  toISODate,
} from '../../lib/calendarDates'
import { formatDateRange } from '../../lib/dateRange'
import { parseTypedDate, parseTypedSpan } from '../../lib/typedDate'
import { formatDate, weekdayLabels } from '../../lib/dateStyle'
import { overlayRoot } from '../../lib/overlay'

/**
 * The one calendar. Every date in the product is picked here rather than in
 * the browser's own `type="date"` control.
 *
 * **Why replace something that already works.** A native picker is three or
 * four actions to land one day — focus the field, open the OS panel, page to
 * the month, click — and it renders in the browser's chrome rather than the
 * app's, so it is the only surface in Wingspace that ignores the account's
 * week-start and date-style settings. It also has no idea a range exists: two
 * fields, two pickers, two trips for what a person thinks of as one span
 * ("tech, third to the seventh").
 *
 * So: one click for a day, click-then-click or a drag for a span. `range`
 * turns the second half on; without it the panel closes the moment a day is
 * chosen, because there is nothing further to say.
 *
 * **A date far from today is typed, or jumped to.** The arrows move a month at
 * a time, which is right for "next Thursday" and wrong for a show two years
 * ago: 17 Oct 2024 then 1 Jan 2028 was forty-three clicks (Alex, 2026-09-26).
 * So the panel opens with a box to type into — "17/10/2024", "Oct 17 2024",
 * or a span in a range field — that moves the calendar as it reads and saves
 * on Enter; and its title is a button, days to months to years, which puts any
 * date about six clicks away without typing at all.
 *
 * Positioned against the **app frame** like `AnchoredMenu`, for the reason
 * given there — measuring against the viewport puts a panel over the header
 * as soon as the body is scrolled. The scrim under it is also why this needs
 * no coordination with an open menu: the first click anywhere dismisses
 * whatever was open, so two floating panels cannot both be reachable.
 */

export interface DatePopoverSpec {
  /** ISO "YYYY-MM-DD", or null for a date not yet set. */
  start: string | null
  /** The other end of a span. Ignored unless `range`. */
  end?: string | null
  /**
   * Where to open while there's no date yet — ISO, and only a month is read
   * from it. A project's Ends opens on its Starts month: an empty end date
   * used to open on today, so a run beginning two years ago was sixteen clicks
   * back before its last night could be picked (2026-09-26).
   */
  openOn?: string | null
  /** Allow a span rather than a single day. */
  range?: boolean
  /**
   * Offer "Clear". Off for a required field — a hidden input carries the value
   * to the server and `required` means nothing on one of those, so the way to
   * keep a required date filled is to not hand out the means to empty it.
   */
  clearable?: boolean
  /** Both ends, ISO or null. `end` is null whenever the span is one day. */
  onPick: (start: string | null, end: string | null) => void
}

// A finger is not a mouse pointer. 30px cells are comfortable to click and
// genuinely hard to tap — well under the 44px both platforms ask for — and a
// calendar is a grid of forty small targets where hitting the neighbour means
// the wrong day rather than nothing. `pointer: coarse` is the question worth
// asking rather than viewport width: a small window on a laptop still has a
// mouse in it, and a tablet at 1024px still has a thumb.
//
// 38 rather than 44 because seven 44px columns plus padding is 330px, which
// leaves a 375px phone almost no margin and would have the panel hard against
// both edges. 38 keeps the whole thing at 288 and the tap target within reach
// of the guideline once the row gap either side is counted.
function cellSize(): number {
  if (typeof window === 'undefined' || !window.matchMedia) return 30
  return window.matchMedia('(pointer: coarse)').matches ? 38 : 30
}

function panelWidth(cell: number): number {
  return 7 * cell + 22
}

let root: Root | null = null
let container: HTMLElement | null = null

// The unmount is deferred by a microtask, which is not fussiness: closing
// happens from a native `pointerup` listener that has just called `onPick`, so
// React is part-way through rendering the *parent* tree when this runs.
// Unmounting another root inside that window is a no-op React declines to
// perform, and the symptom is a calendar that commits its range and then sits
// there open. Clearing the module's handles first keeps `openDatePopover`
// re-entrant in the meantime.
function teardown() {
  const dying = root
  const host = container
  root = null
  container = null

  queueMicrotask(() => {
    dying?.unmount()
    host?.remove()
  })
}

/**
 * Open the calendar under `trigger`. Call it from the trigger's own click
 * handler and hand it the element — `event.currentTarget` is exactly right.
 */
export function openDatePopover(trigger: HTMLElement, spec: DatePopoverSpec) {
  const host = overlayRoot()
  if (!host) return

  teardown()
  container = document.createElement('div')
  host.appendChild(container)
  root = createRoot(container)
  root.render(<DatePopover trigger={trigger} spec={spec} onClose={teardown} />)
}

export function closeDatePopover() {
  teardown()
}

// Six week rows is the worst case for a month grid, and reserving it here
// rather than measuring means the flip-above decision is made before anything
// is painted — a panel that opens downwards and then jumps is worse than one
// that opens upwards.
function place(trigger: HTMLElement, cell: number) {
  const host = overlayRoot()
  const frame = host?.getBoundingClientRect() ?? new DOMRect(0, 0, 1440, 900)
  const at = trigger.getBoundingClientRect()

  const width = panelWidth(cell)
  const h = 6 * cell + 106

  let x = at.left - frame.left
  if (x + width > frame.width - 14) x = Math.max(14, frame.width - width - 14)

  let y = at.bottom - frame.top + 6
  if (y + h > frame.height - 14) y = Math.max(14, at.top - frame.top - h - 6)

  return { x, y }
}

/** Whichever way round they were clicked, a span reads start-first. */
function ordered(a: Date, b: Date): [Date, Date] {
  return a <= b ? [a, b] : [b, a]
}

function DatePopover({
  trigger,
  spec,
  onClose,
}: {
  trigger: HTMLElement
  spec: DatePopoverSpec
  onClose: () => void
}) {
  // Measured once, when the panel opens: a pointer does not change kind
  // mid-calendar, and re-reading it per render would only risk the grid
  // resizing under a drag.
  const [cell] = useState(cellSize)
  const [at] = useState(() => place(trigger, cell))

  // The chosen span, held here rather than read back from `spec` — the panel
  // is opened with a snapshot, so a day clicked a moment ago would otherwise
  // never be painted as chosen.
  const [chosen, setChosen] = useState<{ start: Date | null; end: Date | null }>(() => ({
    start: spec.start ? parseISODate(spec.start) : null,
    end: spec.end ? parseISODate(spec.end) : null,
  }))
  const startDate = chosen.start
  const endDate = chosen.end

  const [month, setMonth] = useState(() => startDate ?? (spec.openOn ? parseISODate(spec.openOn) : new Date()))
  // The first half of a span, while the second is still being chosen. Distinct
  // from the committed start: until the span closes there is nothing to hand
  // back, and drawing from the committed value would make the old range
  // flicker under the new one.
  const [anchor, setAnchor] = useState<Date | null>(null)
  const [hovered, setHovered] = useState<Date | null>(null)
  const hoverRef = useRef<Date | null>(null)
  const [focused, setFocused] = useState<Date>(
    () => startDate ?? (spec.openOn ? parseISODate(spec.openOn) : new Date()),
  )
  const grid = useRef<HTMLDivElement>(null)

  // Which grid the body shows. The title steps outwards — days, then the
  // months of that year, then a dozen years — and choosing steps back in.
  const [view, setView] = useState<'days' | 'months' | 'years'>('days')
  const [typed, setTyped] = useState('')
  const [unreadable, setUnreadable] = useState(false)
  const typing = useRef<HTMLInputElement>(null)

  // Typing is the quick way to a far-off date, so the box has focus on open —
  // except on a touchscreen, where focusing a text box throws the keyboard over
  // half the screen before anybody has asked to type. There the grid has it,
  // as it always did, and the arrow keys still walk it.
  // Without scrolling: the panel already sits under whatever opened it, and
  // letting focus pull its own scroll could shift the page the frame lives in.
  useEffect(() => {
    if (cell === 30) typing.current?.focus({ preventScroll: true })
    else grid.current?.focus({ preventScroll: true })
  }, [cell])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [onClose])

  // Releasing the pointer ends a **drag** — and only a drag. A plain click
  // raises a pointerup too, and treating that as the end of the span would
  // disarm the calendar between the two clicks of click-then-click, which is
  // how anyone picking a span on a touchscreen does it. So a release only
  // closes the span if the pointer actually travelled to a different day.
  useEffect(() => {
    if (anchor === null) return

    function onUp() {
      const over = hoverRef.current
      if (over === null || isSameDay(over, anchor as Date)) return

      const [from, to] = ordered(anchor as Date, over)
      setAnchor(null)
      commit(from, to)
      onClose()
    }

    document.addEventListener('pointerup', onUp)
    return () => document.removeEventListener('pointerup', onUp)
  })

  function commit(from: Date, to: Date | null) {
    const single = to === null || isSameDay(from, to)
    setChosen({ start: from, end: single ? null : to })
    spec.onPick(toISODate(from), single ? null : toISODate(to as Date))
  }

  function pick(day: Date) {
    setFocused(day)

    if (!spec.range) {
      commit(day, null)
      onClose()
      return
    }

    // First click of a span arms it and writes the day immediately, so a
    // single-day date is done in one click and the second is only needed by
    // somebody who actually wants a span.
    if (anchor === null) {
      setAnchor(day)
      commit(day, null)
      return
    }

    const [from, to] = ordered(anchor, day)
    setAnchor(null)
    commit(from, to)
    onClose()
  }

  // What the box reads as, for the calendar to follow and for Enter to save.
  function readTyped(text: string) {
    if (spec.range) return parseTypedSpan(text)
    const day = parseTypedDate(text)
    return day ? { start: day, end: day } : null
  }

  function onType(text: string) {
    setTyped(text)
    setUnreadable(false)
    const read = readTyped(text)
    if (!read) return
    // Follow along: the calendar shows the month being typed and rings the day,
    // so Enter saves something already visible.
    setView('days')
    setMonth(read.start)
    setFocused(read.start)
  }

  function onTypedKey(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      grid.current?.focus()
      return
    }
    if (event.key !== 'Enter') return

    event.preventDefault()
    const read = readTyped(typed)
    if (!read) {
      setUnreadable(true)
      return
    }
    setAnchor(null)
    commit(read.start, isSameDay(read.start, read.end) ? null : read.end)
    onClose()
  }

  // The title's own arrows move by whatever the body is showing.
  const stepMonths = view === 'days' ? 1 : view === 'months' ? 12 : 144
  // A dozen years around the one on show, weighted to the past: most dates
  // typed into Wingspace are jobs already done, so from 2028 the page is
  // 2021–2032 rather than 2028–2039, which would hide the year before it.
  const yearsFrom = month.getFullYear() - 7

  function onKeyDown(event: React.KeyboardEvent) {
    const moves: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    }
    const delta = moves[event.key]

    if (delta !== undefined) {
      event.preventDefault()
      const next = addDays(focused, delta)
      setFocused(next)
      if (next.getMonth() !== month.getMonth()) setMonth(next)
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      pick(focused)
    }
  }

  // What to paint as selected: the live drag if there is one, the committed
  // pair otherwise.
  const previewEnd = anchor !== null ? (hovered ?? focused) : null
  const [spanFrom, spanTo] =
    anchor !== null && previewEnd
      ? ordered(anchor, previewEnd)
      : startDate
        ? [startDate, endDate ?? startDate]
        : [null, null]

  function stateOf(day: Date) {
    if (!spanFrom || !spanTo) return 'none'
    if (day < spanFrom || day > spanTo) return 'none'
    if (isSameDay(spanFrom, spanTo)) return 'only'
    if (isSameDay(day, spanFrom)) return 'start'
    if (isSameDay(day, spanTo)) return 'end'
    return 'middle'
  }

  const today = new Date()

  return (
    <>
      <div
        className="tab"
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, zIndex: 44, cursor: 'default' }}
      />
      <div
        className="fr animate-row-in"
        role="dialog"
        aria-label="Choose a date"
        style={{
          position: 'absolute',
          left: at.x,
          top: at.y,
          width: panelWidth(cell),
          padding: 11,
          borderRadius: 12,
          border: '1px solid var(--w-line-strong)',
          background: 'var(--w-sheet)',
          boxShadow: 'var(--w-float)',
          zIndex: 45,
        }}
      >
        <input
          ref={typing}
          type="text"
          value={typed}
          onChange={(event) => onType(event.target.value)}
          onKeyDown={onTypedKey}
          aria-label={spec.range ? 'Type a date or a span' : 'Type a date'}
          aria-invalid={unreadable || undefined}
          placeholder={spec.range ? '17/10/2024 – 1/1/2028' : 'Type a date — 17/10/2024'}
          autoComplete="off"
          spellCheck={false}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            marginBottom: unreadable ? 4 : 9,
            padding: '6px 8px',
            borderRadius: 8,
            border: `1px solid ${unreadable ? 'var(--color-danger)' : 'var(--w-line-strong)'}`,
            background: 'var(--w-sunk)',
            color: 'var(--color-text)',
            font: '500 12px Manrope,sans-serif',
            outline: 'none',
          }}
        />
        {unreadable && (
          <p role="alert" style={{ margin: '0 0 8px', font: '500 11px Manrope,sans-serif', color: 'var(--color-danger)' }}>
            Couldn’t read that as a date — try 17/10/2024 or 17 Oct 2024.
          </p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
          <Arrow
            label={view === 'days' ? 'Previous month' : view === 'months' ? 'Previous year' : 'Earlier years'}
            onClick={() => setMonth(addMonths(month, -stepMonths))}
          >
            ‹
          </Arrow>
          <button
            type="button"
            aria-live="polite"
            aria-label={
              view === 'days'
                ? `${formatMonthLabel(month)} — choose a month`
                : view === 'months'
                  ? `${month.getFullYear()} — choose a year`
                  : `${yearsFrom} to ${yearsFrom + 11}`
            }
            onClick={() => setView(view === 'days' ? 'months' : 'years')}
            disabled={view === 'years'}
            style={{
              flex: 1,
              textAlign: 'center',
              font: '600 12px Manrope,sans-serif',
              color: 'var(--color-text)',
              background: 'none',
              border: 'none',
              borderRadius: 6,
              padding: '3px 0',
              cursor: view === 'years' ? 'default' : 'pointer',
            }}
          >
            {view === 'days'
              ? formatMonthLabel(month)
              : view === 'months'
                ? month.getFullYear()
                : `${yearsFrom} – ${yearsFrom + 11}`}
          </button>
          <Arrow
            label={view === 'days' ? 'Next month' : view === 'months' ? 'Next year' : 'Later years'}
            onClick={() => setMonth(addMonths(month, stepMonths))}
          >
            ›
          </Arrow>
        </div>

        {view !== 'days' && (
          <div
            role="grid"
            aria-label={view === 'months' ? String(month.getFullYear()) : `${yearsFrom} to ${yearsFrom + 11}`}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gridAutoRows: '1fr',
              gap: 4,
              // The days' height, so stepping in and out doesn't make the panel jump.
              height: 20 + 6 * cell,
            }}
          >
            {Array.from({ length: 12 }, (_, index) => {
              const value =
                view === 'months'
                  ? new Date(month.getFullYear(), index, 1)
                  : new Date(yearsFrom + index, month.getMonth(), 1)
              const holds = (date: Date | null) =>
                date !== null &&
                date.getFullYear() === value.getFullYear() &&
                (view === 'years' || date.getMonth() === value.getMonth())
              const chosen = holds(startDate) || holds(endDate)
              const current = holds(today)

              return (
                <button
                  key={index}
                  type="button"
                  role="gridcell"
                  aria-label={view === 'months' ? formatMonthLabel(value) : String(value.getFullYear())}
                  aria-selected={chosen}
                  onClick={() => {
                    setMonth(value)
                    setView(view === 'months' ? 'days' : 'months')
                  }}
                  style={{
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    font: `${chosen ? 600 : 500} 12px Manrope,sans-serif`,
                    color: chosen ? 'var(--w-onaccent)' : 'var(--color-text)',
                    background: chosen ? 'var(--color-accent)' : 'transparent',
                    boxShadow: current && !chosen ? 'inset 0 0 0 1px var(--color-accent)' : undefined,
                  }}
                >
                  {view === 'months' ? value.toLocaleDateString('en-GB', { month: 'short' }) : value.getFullYear()}
                </button>
              )
            })}
          </div>
        )}

        {view === 'days' && (
          <>

        <div style={{ display: 'grid', gridTemplateColumns: `repeat(7, ${cell}px)` }}>
          {weekdayLabels().map((label) => (
            <span
              key={label}
              aria-hidden
              style={{
                height: 20,
                display: 'grid',
                placeItems: 'center',
                font: '600 10px Manrope,sans-serif',
                color: 'var(--color-faint)',
              }}
            >
              {label.slice(0, 1)}
            </span>
          ))}
        </div>

        {/* One tab stop for the whole grid, arrow keys inside it — 35 tab stops
            is not a calendar, it is an obstacle. */}
        <div
          ref={grid}
          role="grid"
          tabIndex={0}
          onKeyDown={onKeyDown}
          aria-label={formatMonthLabel(month)}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(7, ${cell}px)`,
            outline: 'none',
          }}
        >
          {getMonthGrid(month).map((week) =>
            week.map((day) => {
              const outside = day.getMonth() !== month.getMonth()
              const state = stateOf(day)
              const isFocused = isSameDay(day, focused)

              return (
                <button
                  key={toISODate(day)}
                  type="button"
                  role="gridcell"
                  tabIndex={-1}
                  aria-label={formatDate(day)}
                  aria-selected={state !== 'none'}
                  onPointerDown={(event) => {
                    event.preventDefault()
                    pick(day)
                  }}
                  onPointerEnter={() => {
                    hoverRef.current = day
                    setHovered(day)
                  }}
                  style={{
                    height: cell,
                    border: 'none',
                    cursor: 'pointer',
                    font: `${state === 'none' ? 500 : 600} 12px Manrope,sans-serif`,
                    color: paintedInk(state, outside),
                    background: paintedFill(state),
                    // Only the ends are rounded, so a span reads as one shape
                    // rather than as seven separate chips.
                    borderRadius: radiusFor(state),
                    boxShadow: isFocused ? 'inset 0 0 0 1px var(--color-accent)' : undefined,
                    outline: 'none',
                  }}
                >
                  <span
                    style={{
                      display: 'grid',
                      placeItems: 'center',
                      height: '100%',
                      // Today keeps its mark inside a selection, where an
                      // underline in accent ink would vanish against the fill.
                      borderBottom: isSameDay(day, today)
                        ? `1.5px solid ${state === 'none' ? 'var(--color-accent)' : 'currentColor'}`
                        : '1.5px solid transparent',
                    }}
                  >
                    {day.getDate()}
                  </span>
                </button>
              )
            }),
          )}
        </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
          <Foot
            onClick={() => {
              setMonth(new Date())
              setFocused(new Date())
              pick(new Date())
            }}
          >
            Today
          </Foot>
          <span style={{ flex: 1 }} />
          {spec.clearable !== false && (
            <Foot
              onClick={() => {
                spec.onPick(null, null)
                onClose()
              }}
            >
              Clear
            </Foot>
          )}
        </div>
      </div>
    </>
  )
}

type CellState = 'none' | 'only' | 'start' | 'end' | 'middle'

function paintedFill(state: CellState): string | undefined {
  if (state === 'none') return 'transparent'
  if (state === 'middle') return 'var(--w-sunk)'
  return 'var(--color-accent)'
}

function paintedInk(state: CellState, outside: boolean): string {
  if (state === 'only' || state === 'start' || state === 'end') return 'var(--w-onaccent)'
  if (state === 'middle') return 'var(--color-text)'
  return outside ? 'var(--color-faint)' : 'var(--color-text)'
}

function radiusFor(state: CellState): string {
  switch (state) {
    case 'only':
      return '8px'
    case 'start':
      return '8px 0 0 8px'
    case 'end':
      return '0 8px 8px 0'
    default:
      return '0'
  }
}

function Arrow({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      className="tab"
      aria-label={label}
      onClick={onClick}
      style={{
        width: 24,
        height: 24,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 7,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        font: '600 15px Manrope,sans-serif',
        color: 'var(--color-muted)',
      }}
    >
      {children}
    </button>
  )
}

function Foot({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="tab"
      onClick={onClick}
      style={{
        padding: '5px 9px',
        borderRadius: 7,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        font: '600 11px Manrope,sans-serif',
        color: 'var(--color-action-ink)',
      }}
    >
      {children}
    </button>
  )
}

/**
 * The control that stands where an `<input type="date">` used to.
 *
 * A button rather than a field, because there is nothing to type into it — and
 * the label is the date written the account's own way ("7 Jul 2025",
 * "3 – 7 Apr 2026"), which is the other half of what the native control could
 * not do. `style` is passed through so a call site can keep its own field
 * treatment, the same arrangement `MenuTrigger` has.
 */
export function DateField({
  start,
  end,
  openOn,
  range,
  clearable,
  onPick,
  ariaLabel,
  placeholder = 'Pick a date',
  className,
  style,
}: DatePopoverSpec & {
  ariaLabel: string
  placeholder?: string
  className?: string
  style?: React.CSSProperties
}) {
  const shown = start ? formatDateRange(start, range ? end : null) : null

  return (
    <button
      type="button"
      className={className}
      aria-label={ariaLabel}
      onClick={(event) =>
        openDatePopover(event.currentTarget, { start, end, openOn, range, clearable, onPick })
      }
      style={{
        textAlign: 'left',
        cursor: 'pointer',
        color: shown ? 'var(--color-text)' : 'var(--color-faint)',
        ...style,
      }}
    >
      {shown ?? placeholder}
    </button>
  )
}

/**
 * The calendar wearing an `<input type="date">`'s clothes.
 *
 * Everything that used one is a `Field`, and a Field is used two ways: with
 * `value`/`onChange` as a controlled React input, and with `name`/`defaultValue`
 * as a plain form control Rails reads on submit. Some are both at once — a
 * contract's phase rows are controlled *and* posted. So this takes the props an
 * input takes and keeps all three arrangements working:
 *
 * - `onChange` is called with `{ target: { value, name } }`, which is what every
 *   call site reads and the only part of the event any of them touch.
 * - a hidden input carries the value whenever there is a `name`, so the
 *   surrounding form submits exactly what it submitted before.
 *
 * The one thing that does not survive is browser-level `required`: the spec
 * says a hidden input is never validated. Those fields are validated on the
 * server already and render the error inline, and `clearable` is switched off
 * for them so the picker cannot be the thing that empties one.
 */
export function DateInput({
  name,
  value,
  defaultValue,
  onChange,
  required,
  ariaLabel,
  placeholder,
  style,
}: {
  name?: string
  value?: string | number | readonly string[]
  defaultValue?: string | number | readonly string[]
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  required?: boolean
  ariaLabel: string
  placeholder?: string
  style?: React.CSSProperties
}) {
  const controlled = value !== undefined
  const [held, setHeld] = useState(() => String(defaultValue ?? ''))
  const iso = controlled ? String(value ?? '') : held

  function pick(next: string | null) {
    const chosen = next ?? ''
    if (!controlled) setHeld(chosen)
    onChange?.({ target: { value: chosen, name: name ?? '' } } as React.ChangeEvent<HTMLInputElement>)
  }

  return (
    <>
      <DateField
        start={iso || null}
        onPick={(start) => pick(start)}
        clearable={!required}
        ariaLabel={ariaLabel}
        placeholder={placeholder}
        style={style}
      />
      {name && <input type="hidden" name={name} value={iso} readOnly />}
    </>
  )
}
